import React from 'react';
import { Chip } from '../ui/Chip';
import { formatCurrency } from '../../utils/formatCurrency';
import AcceptedConfigurationModal from './AcceptedConfigurationModal';

/**
 * LockedAtAcceptancePill — PT-43.2
 *
 * Renders a "🔒 Locked at acceptance" chip on Quote / Job / Invoice detail
 * pages when the underlying entity carries acceptance-lock provenance
 * (`lockedTotalCents` and/or `acceptedConfigurationId`, stamped by the
 * money-path 38.3 code and PT-42's price-mutation guard).
 *
 * Purpose (43.2 + 43.3 pairing): the pill answers *"why can't I edit this?"*
 * BEFORE the user tries and fails. Paired with the PT-43.1 "Create Change
 * Order" route which answers *"what do I do instead?"*
 *
 * Contract:
 * - Returns `null` when neither `lockedTotalCents` nor `acceptedConfigurationId`
 *   is present. This is the load-bearing branch — the seven-defect shape from
 *   the standing rules is a field written with care but never consumed, and
 *   the mirror image is a consumer that renders a placeholder when there's
 *   no lock. We render NOTHING in the no-lock case.
 * - Accepts both camelCase and snake_case (backend has been going through a
 *   normalization; readers should tolerate both).
 * - `formatCurrency` expects cents (integer) — no divide-by-100 here.
 *
 * Props:
 * @param {number|null} [lockedTotalCents]           - Frozen total in cents (PT-30 zero-is-valid)
 * @param {number|null} [locked_total_cents]         - snake_case alias
 * @param {string} [acceptedConfigurationID]         - Snapshot config id (Go PascalCase over JSON)
 * @param {string} [acceptedConfigurationId]         - camelCase (frontend canonical)
 * @param {string} [accepted_configuration_id]       - snake_case alias
 * @param {string} [acceptedAt]                      - Optional ISO timestamp for tooltip
 * @param {string} [accepted_at]                     - snake_case alias
 * @param {string} [quoteId]                          - IDI-116: when provided, the pill
 *   becomes clickable and opens the full acceptance record (signature image,
 *   accepted line items, terms) via AcceptedConfigurationModal. Omit to keep
 *   the pill display-only (e.g. a context where the source quote isn't known).
 */
export function LockedAtAcceptancePill(props) {
  // IDI-116: hook must run unconditionally (React rules-of-hooks) —
  // declared before the early-return-null branch below, which is otherwise
  // unaffected (this component still renders nothing when there's no lock).
  const [modalOpen, setModalOpen] = React.useState(false);

  // Zero-is-valid: use nullish coalescing across all three casings, then
  // check `!= null` (matches both undefined and null but preserves 0).
  const lockedCentsRaw =
    props.lockedTotalCents ??
    props.locked_total_cents ??
    null;
  const lockedCents = lockedCentsRaw == null ? null : Number(lockedCentsRaw);

  const acceptedId =
    props.acceptedConfigurationId ||
    props.acceptedConfigurationID ||
    props.accepted_configuration_id ||
    '';

  const acceptedAt = props.acceptedAt || props.accepted_at || '';

  // No lock, no pill. This is the whole point.
  if (lockedCents == null && !acceptedId) {
    return null;
  }

  // Build the tooltip. Frozen total is the primary signal; acceptedAt (if
  // present) is a secondary breadcrumb.
  const tooltipParts = [];
  if (lockedCents != null) {
    tooltipParts.push(`Frozen total: ${formatCurrency(lockedCents)}`);
  }
  if (acceptedAt) {
    tooltipParts.push(`Accepted: ${acceptedAt}`);
  }
  tooltipParts.push('Create a Change Order to modify pricing.');
  const tooltip = tooltipParts.join(' · ');

  // IDI-116: quoteId presence is the ONLY thing that makes this pill
  // interactive. Without it we have no way to know which quote's acceptance
  // record to fetch, so the pill stays exactly as it was — a tooltip-only
  // status chip — rather than opening a modal that can't load anything.
  const clickable = Boolean(props.quoteId);

  return (
    <>
      <Chip
        color="success"
        title={tooltip}
        aria-label={clickable ? `${tooltip} — click to view the signed acceptance` : tooltip}
        data-testid="locked-at-acceptance-pill"
        onClick={clickable ? () => setModalOpen(true) : undefined}
      >
        🔒 Locked at acceptance
      </Chip>
      {clickable && (
        <AcceptedConfigurationModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          quoteId={props.quoteId}
        />
      )}
    </>
  );
}

export default LockedAtAcceptancePill;
